import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, X, Send, Trash2, Loader2, ChevronDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SESSION_KEY = 'lsn_ai_session_id';

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const SUGGESTIONS = [
  "Which assets broke down most this month?",
  "What parts are running low on stock?",
  "Are there any overdue maintenance jobs?",
  "Summarise today's operations",
  "Which vendor should I reorder from?",
];

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="h-7 w-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
        </div>
      )}
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? 'bg-amber-500 text-slate-900 rounded-br-sm font-medium'
          : 'bg-slate-700 text-slate-100 rounded-bl-sm'
      }`}>
        {msg.content}
      </div>
    </div>
  );
}

export default function AIChatWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getSessionId);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Load history on open
  useEffect(() => {
    if (open && messages.length === 0) {
      loadHistory();
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/ai/chat/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch {
      // fresh session
    }
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/ai/chat`, {
        message: msg,
        session_id: sessionId,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      toast.error('AI assistant unavailable');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/ai/chat/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      localStorage.removeItem(SESSION_KEY);
    } catch {
      toast.error('Failed to clear chat');
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="ai-chat-toggle"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-amber-500 shadow-lg shadow-amber-500/30 flex items-center justify-center hover:bg-amber-400 transition-all hover:scale-105 active:scale-95"
      >
        {open
          ? <ChevronDown className="h-6 w-6 text-slate-900" />
          : <Bot className="h-6 w-6 text-slate-900" />
        }
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 right-6 z-50 w-[370px] max-h-[600px] flex flex-col rounded-2xl bg-slate-800 border border-slate-600 shadow-2xl shadow-slate-900/60 overflow-hidden"
          style={{ maxHeight: 'calc(100vh - 140px)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-slate-900 border-b border-slate-700 flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">LSN Operations AI</p>
              <p className="text-xs text-slate-400">Powered by live data</p>
            </div>
            <button onClick={clearChat} className="text-slate-500 hover:text-slate-300 p-1" title="Clear chat">
              <Trash2 className="h-4 w-4" />
            </button>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300 p-1">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
            {messages.length === 0 && !loading && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="h-7 w-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                  </div>
                  <div className="bg-slate-700 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-slate-100">
                    Hi {user?.name?.split(' ')[0] || 'there'}! I can analyse your maintenance, inventory and procurement data. What would you like to know?
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center pt-1">Try asking:</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map(s => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-full px-3 py-1.5 transition-colors text-left"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}

            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="h-7 w-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                </div>
                <div className="bg-slate-700 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1 items-center">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 p-3 border-t border-slate-700 bg-slate-900 flex-shrink-0">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask about your operations..."
              className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
              data-testid="ai-chat-input"
              disabled={loading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="h-9 w-9 rounded-xl bg-amber-500 flex items-center justify-center hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              data-testid="ai-chat-send"
            >
              {loading
                ? <Loader2 className="h-4 w-4 text-slate-900 animate-spin" />
                : <Send className="h-4 w-4 text-slate-900" />
              }
            </button>
          </div>
        </div>
      )}
    </>
  );
}
